import { Component, Layout, LookupModelSelectionEvent, TableRow, Textbox } from "@mcleod/components";

export interface ContactLookupProps {
    contactNameField: string;
    contactPhoneField?: string;
    contactEmailField?: string;
    contactTitleField?: string;
    nameTextbox: Textbox;
    phoneTextbox?: Textbox;
    emailTextbox?: Textbox;
    titleTextbox?: Textbox;
    phoneTextboxId?: string;
    emailTextboxId?: string;
    titleTextboxId?: string;
    onLookupModelSelection?: (event: LookupModelSelectionEvent) => void;
}

export interface LayoutContactLookupProps extends ContactLookupProps {
    layout: Layout;
}

// Used to track relevant information for selected contacts when a multi-select lookup model is used and not all information is stored.
export interface SelectedContactProps {
    id: string;
    name?: string;
    title?: string;
    phone?: string;
    email?: string;
}

const CONTACT_PHONE_FIELD = "phone";
const CONTACT_EMAIL_FIELD = "email";
const CONTACT_TITLE_FIELD = "title";

export class ContactUtil {

    static configureTableRowContactLookup(props: ContactLookupProps): void {
        props.nameTextbox.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.tableRowContactNameLookupModelSelection(event, props);
            if (props.onLookupModelSelection) {
                props.onLookupModelSelection(event);
            }
        });
    }

    static configureLayoutContactLookup(props: LayoutContactLookupProps): void {
        props.nameTextbox.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.layoutContactNameLookupModelSelection(event, props);
            if (props.onLookupModelSelection) {
                props.onLookupModelSelection(event);
            }
        });
    }

    static layoutContactNameLookupModelSelection(event: LookupModelSelectionEvent, props: LayoutContactLookupProps) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            props.layout.activeRow.setLookupModelData(props.contactNameField, selectedRow);
            if (props.contactPhoneField) {
                props.layout.activeRow?.set(props.contactPhoneField, selectedRow.get(CONTACT_PHONE_FIELD));
            }
            if (props.contactEmailField) {
                props.layout.activeRow?.set(props.contactEmailField, selectedRow.get(CONTACT_EMAIL_FIELD));
            }
            if (props.contactTitleField) {
                props.layout.activeRow?.set(props.contactTitleField, selectedRow.get(CONTACT_TITLE_FIELD));
            }
            props.layout.mainDataSource?.displayDataInBoundComponents();
        } else if (event.hasDeselection()) {
            if (props.contactPhoneField) {
                props.layout.activeRow?.set(props.contactPhoneField, null);
            }
            if (props.contactEmailField) {
                props.layout.activeRow?.set(props.contactEmailField, null);
            }
            if (props.contactTitleField) {
                props.layout.activeRow?.set(props.contactTitleField, null);
            }
            props.layout.mainDataSource?.displayDataInBoundComponents();
        }
    }

    static tableRowContactNameLookupModelSelection(event: LookupModelSelectionEvent, props: ContactLookupProps) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data;
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            modelRow.setLookupModelData(props.contactNameField, selectedRow);

            if (props.contactPhoneField) {
                modelRow.set(props.contactPhoneField, selectedRow.get(CONTACT_PHONE_FIELD));
            }
            if (props.phoneTextboxId) {
                const textboxPhone = tableRow.findComponentById(props.phoneTextboxId) as Textbox;
                if (textboxPhone) {
                    textboxPhone.text = selectedRow.get(CONTACT_PHONE_FIELD);
                }
            }

            if (props.contactEmailField) {
                modelRow.set(props.contactEmailField, selectedRow.get(CONTACT_EMAIL_FIELD));
            }
            if (props.emailTextboxId) {
                const textboxContactEmail = tableRow.findComponentById(props.emailTextboxId) as Textbox;
                if (textboxContactEmail) {
                    textboxContactEmail.text = selectedRow.get(CONTACT_EMAIL_FIELD);
                }
            }

            if (props.contactTitleField) {
                modelRow.set(props.contactTitleField, selectedRow.get(CONTACT_TITLE_FIELD));
            }
            if (props.titleTextboxId) {
                const textboxContactTitle = tableRow.findComponentById(props.titleTextboxId) as Textbox;
                if (textboxContactTitle) {
                    textboxContactTitle.text = selectedRow.get(CONTACT_TITLE_FIELD);
                }
            }
        } else if (event.hasDeselection()) {
            if (props.contactPhoneField) {
                modelRow.set(props.contactPhoneField, null);
            }
            if (props.phoneTextboxId) {
                const textboxPhone = tableRow.findComponentById(props.phoneTextboxId) as Textbox;
                if (textboxPhone) {
                    textboxPhone.text = null;
                }
            }

            if (props.contactEmailField) {
                modelRow.set(props.contactEmailField, null);
            }
            if (props.emailTextboxId) {
                const textboxContactEmail = tableRow.findComponentById(props.emailTextboxId) as Textbox;
                if (textboxContactEmail) {
                    textboxContactEmail.text = null;
                }
            }

            if (props.contactTitleField) {
                modelRow.set(props.contactTitleField, null);
            }
            if (props.titleTextboxId) {
                const textboxContactTitle = tableRow.findComponentById(props.titleTextboxId) as Textbox;
                if (textboxContactTitle) {
                    textboxContactTitle.text = null;
                }
            }
        }
    }

    static layoutContactNameMultiSelectLookupModelSelection(event: LookupModelSelectionEvent, selectedContacts: SelectedContactProps[], props: LayoutContactLookupProps) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const newContact: SelectedContactProps = {
                id: event.getValueFromSelectedRow("id", selectedRow),
                name: event.getValueFromSelectedRow("name", selectedRow),
                email: event.getValueFromSelectedRow("email", selectedRow),
                phone: event.getValueFromSelectedRow("phone", selectedRow),
                title: event.getValueFromSelectedRow("title", selectedRow)
            };
            selectedContacts.push(newContact);
        } else if (event.deselection) {
            const deselectedId = event.getValueFromDeselectedRow("id", event.deselection);
            const indexToRemove = selectedContacts.findIndex(contact => contact.id === deselectedId);
            if (indexToRemove >= 0) {
                selectedContacts.splice(indexToRemove, 1);
            }
        }

        // When a lookup allows multiple contacts to be selected, additional fields will correspond to the least recently selected contact
        if (selectedContacts.length > 0) {
            if (props.phoneTextbox) {
                props.phoneTextbox.text = selectedContacts[0].phone || null;
            }
            if (props.emailTextbox) {
                props.emailTextbox.text = selectedContacts[0].email || null;
            }
            if (props.titleTextbox) {
                props.titleTextbox.text = selectedContacts[0].title || null;
            }
        } else {
            // Clear fields when no contacts are selected
            if (props.phoneTextbox) {
                props.phoneTextbox.text = null;
            }
            if (props.emailTextbox) {
                props.emailTextbox.text = null;
            }
            if (props.titleTextbox) {
                props.titleTextbox.text = null;
            }
        }
    }
}
